
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Field = ({ label, error, children, fullWidth = false }) => (
  <div className={fullWidth ? 'md:col-span-2' : ''}>
    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{label}</label>
    {children}
    {error && <span className="text-red-500 text-xs mt-1 block">{error}</span>}
  </div>
);

const RentalForm = ({ requestsType, equipmentModels, onSubmit, onCancel, userId, hasSubscription }) => {
  const [requestTypeId, setRequestTypeId] = useState('');
  const [equipmentId, setEquipmentId] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [errors, setErrors] = useState({});
  const [contactName, setContactName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [details, setDetails] = useState('');
  const [cities, setCities] = useState([]);
  const [hasForbiddenWord, setHasForbiddenWord] = useState(false);
  const [priceType, setPriceType] = useState('hour');
  const [priceValue, setPriceValue] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const forbiddenWords = ['путин', 'украина', 'наркотик'];

  const checkForForbiddenWords = (text) => {
    const words = text.toLowerCase().split(/\s+/);
    return words.some(word => {
      const cleanedWord = word.replace(/[^a-zа-яё0-9]/g, '');
      return forbiddenWords.includes(cleanedWord);
    });
  };

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(API_URL + '/citys');
        const data = await response.json();
        setCities(data);
      } catch (error) {
        console.error('Ошибка загрузки городов:', error);
      }
    };
    fetchCities();
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Файл слишком большой (максимум 5 MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const token = localStorage.getItem('token');
      const response = await fetch(API_URL + '/upload', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Ошибка загрузки');
      setPhotoUrl(data.photo_url);
    } catch (error) {
      alert('Ошибка загрузки фото: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDetailsChange = (e) => {
    const text = e.target.value;
    setDetails(text);
    setHasForbiddenWord(checkForForbiddenWords(text));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (hasForbiddenWord) {
      alert('Исправьте текст перед сохранением.');
      return;
    }

    const newErrors = {};
    if (!requestTypeId) newErrors.requestTypeId = 'Выберите тип заявки';
    if (!equipmentId) newErrors.equipmentId = 'Выберите спецтехнику';
    if (!address.trim()) newErrors.address = 'Введите адрес';
    if (!city) newErrors.city = 'Выберите город';
    if (!contactName) newErrors.contactName = 'Введите контактное лицо';
    if (!phoneNumber) newErrors.phoneNumber = 'Введите номер телефона';
    if (requestTypeId === '2' && (!priceValue || parseFloat(priceValue) <= 0)) {
      newErrors.pricePerHour = 'Укажите цену за час или смену';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!userId) {
      alert('Ошибка: пользователь не авторизован.');
      return;
    }

    const phoneRegex = /^\+7\d{10}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setErrors({ ...errors, phoneNumber: 'Номер телефона должен начинаться с +7 и содержать 11 цифр' });
      return;
    }

    let calculatedPricePerHour = null;
    if (requestTypeId === '2' && priceValue) {
      calculatedPricePerHour = priceType === 'hour' ? parseFloat(priceValue) : parseFloat(priceValue) / 8;
    }

    const formData = {
      user_id: userId,
      type_id: parseInt(requestTypeId),
      equipment_id: parseInt(equipmentId),
      date_requested: new Date().toISOString().split('T')[0],
      status: 'new',
      address,
      city,
      contact_name: contactName,
      phone_number: phoneNumber,
      photo_url: photoUrl,
      details,
      price_per_hour: calculatedPricePerHour
    };

    onSubmit(formData);
    setErrors({});
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-black text-slate-900 m-0 uppercase tracking-tight">
            {requestTypeId === '2' ? '📢 Разместить объявление' : '📝 Создать заявку'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {requestTypeId === '2' ? 'Сдайте свою технику в аренду' : 'Найдите технику для своих задач'}
          </p>
        </div>

        {requestTypeId === '2' && !hasSubscription && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 mb-6 text-center">
            <div className="text-3xl mb-2">⚠️</div>
            <div className="text-base font-bold text-amber-800 mb-2">Требуется подписка</div>
            <div className="text-sm text-amber-700 mb-4">Для размещения объявлений "Сдаю технику" нужна активная подписка</div>
            <button
              type="button"
              onClick={() => { onCancel(); setTimeout(() => window.dispatchEvent(new CustomEvent('goToTariffs')), 100); }}
              className="btn btn-primary"
            >
              ⭐ Посмотреть тарифы
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Field label="Тип заявки" error={errors.requestTypeId}>
              <select value={requestTypeId} onChange={(e) => setRequestTypeId(e.target.value)} className={errors.requestTypeId ? 'error' : ''}>
                <option value="">Выберите тип</option>
                {Object.entries(requestsType).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </Field>

            <Field label="Спецтехника" error={errors.equipmentId}>
              <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} className={errors.equipmentId ? 'error' : ''}>
                <option value="">Выберите технику</option>
                {Object.entries(equipmentModels).map(([id, model]) => (
                  <option key={id} value={id}>{model}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Field label="Город" error={errors.city}>
              <select value={city} onChange={(e) => setCity(e.target.value)} className={errors.city ? 'error' : ''}>
                <option value="">Выберите город</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Адрес" error={errors.address}>
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="ул. Пушкина, д. 10" className={errors.address ? 'error' : ''} />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Field label="Контактное лицо" error={errors.contactName}>
              <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Иван Иванов" className={errors.contactName ? 'error' : ''} />
            </Field>

            <Field label="Телефон" error={errors.phoneNumber}>
              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="+7XXXXXXXXXX" className={errors.phoneNumber ? 'error' : ''} />
            </Field>
          </div>

          {requestTypeId === '2' && (
            <Field label="💰 Стоимость аренды" error={errors.pricePerHour} fullWidth>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-gold transition">
                  <input type="radio" value="hour" checked={priceType === 'hour'} onChange={() => setPriceType('hour')} className="w-auto" />
                  <span className="text-sm font-medium">₽/час</span>
                </label>
                <label className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-gold transition">
                  <input type="radio" value="shift" checked={priceType === 'shift'} onChange={() => setPriceType('shift')} className="w-auto" />
                  <span className="text-sm font-medium">₽/смена (8ч)</span>
                </label>
              </div>
              <input
                type="number"
                value={priceValue}
                onChange={(e) => setPriceValue(e.target.value)}
                placeholder={priceType === 'hour' ? 'Например: 2000' : 'Например: 15000'}
                className={errors.pricePerHour ? 'error' : ''}
                min="0"
                step="100"
              />
            </Field>
          )}

          {requestTypeId === '2' && (
            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">📸 Фото техники</label>
              {photoUrl ? (
                <div className="relative rounded-2xl overflow-hidden border-2 border-gold/40">
                  <img src={photoUrl} alt="Фото" className="w-full h-56 object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotoUrl('')}
                    className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl shadow-lg"
                  >×</button>
                </div>
              ) : (
                <label className="block border-2 border-dashed border-slate-300 hover:border-gold rounded-2xl p-8 text-center cursor-pointer transition bg-slate-50 hover:bg-amber-50">
                  <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" onChange={handlePhotoUpload} className="hidden" disabled={uploading} />
                  <div className="text-5xl mb-3">{uploading ? '⏳' : '📷'}</div>
                  <div className="text-base font-bold text-slate-700 mb-1">{uploading ? 'Загрузка...' : 'Загрузить фото техники'}</div>
                  <div className="text-xs text-slate-500">JPG, PNG, GIF или WEBP • до 5 MB</div>
                </label>
              )}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Детали заявки</label>
            <textarea
              value={details}
              onChange={handleDetailsChange}
              placeholder="Опишите задачу подробнее..."
              rows="3"
              className={hasForbiddenWord ? 'error' : ''}
            />
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={requestTypeId === '2' && !hasSubscription} className="btn btn-primary flex-1">
              💾 Сохранить
            </button>
            <button type="button" onClick={onCancel} className="btn btn-outline px-8">
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RentalForm;